import { HttpClient } from './http-client';

export interface UserDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  active: boolean;
  externalUserId?: string;
  externalProvider?: string;
  createdAt: string;
  updatedAt: string;
  deactivatedAt?: string;
  deactivationReason?: string;
  divisionRoles: UserRoleDto[];
  // Global (not division-scoped) roles: approvn-admin, approvn-approver, approvn-customer-support, approvn-picker.
  systemRoles?: string[];
}

export interface UserRoleDto {
  divisionId: string;
  divisionName: string;
  role: string;
  isDivisionAdmin: boolean;
  isWorkflowAdmin: boolean;
}

export interface UserActivityDto {
  userId: string;
  totalOrders: number;
  pendingApprovals: number;
  completedApprovals: number;
  lastLoginAt?: string;
  lastActivityAt?: string;
  recentActivity: ActivityStatDto[];
}

export interface ActivityStatDto {
  date: string;
  activityType: string;
  count: number;
}

export interface UserMatchIdentifierDto {
  id: string;
  userId: string;
  matchIdentifierId: string;
  matchIdentifierName: string;
  jsonPath: string;
  value?: string;
  autoMatch: boolean;
  createdAt: string;
  updatedAt: string;
  workflows: WorkflowAssociationDto[];
}

export interface WorkflowAssociationDto {
  workflowId: string;
  workflowName: string;
  isRequired: boolean;
  weight: number;
  priority: number;
  active: boolean;
}

export interface UserMatchIdentifierListResponse {
  userId: string;
  matchIdentifiers: UserMatchIdentifierDto[];
  activeCount: number;
}

export class UserService {
  constructor(private httpClient: HttpClient) {}

  /**
   * Get current authenticated user's profile
   */
  async getCurrentUser(): Promise<UserDto> {
    return this.httpClient.get<UserDto>('/api/v1/users/me');
  }

  /**
   * Get the current authenticated user's effective roles: global system roles (approvn-admin/
   * approvn-approver/approvn-customer-support/approvn-picker - identical across every
   * division-scoped User row for this email) plus "DivisionAdmin"/"WorkflowAdmin" if any
   * division-scoped User row has a role carrying that capability (driven by
   * UserRoleDto.isDivisionAdmin/isWorkflowAdmin, not the role's name - divisionRoles is
   * per-division, so unlike systemRoles it can differ across the /users/me response's entries).
   */
  async getCurrentUserRoles(): Promise<string[]> {
    const response = await this.httpClient.get<{ users: UserDto[] }>('/api/v1/users/me');
    const roles = new Set(response.users?.[0]?.systemRoles ?? []);
    if (response.users?.some(u => u.divisionRoles?.some(dr => dr.isDivisionAdmin))) {
      roles.add('DivisionAdmin');
    }
    if (response.users?.some(u => u.divisionRoles?.some(dr => dr.isWorkflowAdmin))) {
      roles.add('WorkflowAdmin');
    }
    return Array.from(roles);
  }

  /**
   * Get user by ID
   */
  async getUserById(userId: string): Promise<UserDto> {
    return this.httpClient.get<UserDto>(`/api/v1/users/${userId}`);
  }

  /**
   * Get current user's activity
   */
  async getUserActivity(
    userId: string,
    startDate?: string,
    endDate?: string
  ): Promise<UserActivityDto> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.httpClient.get<UserActivityDto>(`/api/v1/users/${userId}/activity${query}`);
  }

  /**
   * Get user's match identifiers for a specific division
   */
  async getUserMatchIdentifiers(
    divisionId: string,
    userId: string
  ): Promise<UserMatchIdentifierListResponse> {
    return this.httpClient.get<UserMatchIdentifierListResponse>(
      `/api/v1/divisions/${divisionId}/users/${userId}/match-identifiers`
    );
  }

  /**
   * Update user information
   */
  async updateUser(userId: string, data: Partial<UserDto>): Promise<UserDto> {
    return this.httpClient.put<UserDto>(`/api/v1/users/${userId}`, data);
  }
}
